import React, { useState, useEffect, useCallback, useContext } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress,
  TextField, InputAdornment, Tooltip, IconButton, Snackbar, Alert,
  Avatar, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tabs, Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentIcon from '@mui/icons-material/Payment';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { CONTEXTS } from 'figshelf-modules';

const statusColor = (s) => ({ pending: 'warning', approved: 'success', rejected: 'error' }[s] ?? 'default');
const methodLabel = (m) => ({ gcash: 'GCash', bank_transfer: 'Bank Transfer', paypal: 'PayPal', other: 'Other' }[m] ?? m);

const PaymentRequests = () => {
  const api = useContext(CONTEXTS.APIContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialog, setDialog] = useState({ open: false, type: null, request: null });
  const [adminNotes, setAdminNotes] = useState('');
  const [actioning, setActioning] = useState(false);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/premium/admin/payment-requests');
      const raw = res?.data ?? res ?? [];
      setRequests(Array.isArray(raw) ? raw : []);
    } catch { notify('Failed to load payment requests', 'error'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const openAction = (type, request) => {
    setAdminNotes('');
    setDialog({ open: true, type, request });
  };

  const handleAction = async () => {
    const { type, request } = dialog;
    if (!request) return;
    try {
      setActioning(true);
      if (type === 'approve') {
        await api.post(`/premium/admin/payment-requests/${request.id}/approve`, { adminNotes });
        notify(`Premium activated for @${request.user?.username}`);
      } else {
        await api.post(`/premium/admin/payment-requests/${request.id}/reject`, { adminNotes });
        notify(`Request rejected for @${request.user?.username}`, 'warning');
      }
      setDialog({ open: false, type: null, request: null });
      fetchRequests();
    } catch (err) {
      notify(err?.response?.data?.message || 'Action failed', 'error');
    } finally {
      setActioning(false);
    }
  };

  const STATUS_TABS = ['pending', 'approved', 'rejected'];

  const filtered = requests.filter((r) => {
    const matchTab = r.status === STATUS_TABS[tab];
    if (!matchTab) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.user?.username?.toLowerCase().includes(q) ||
      r.user?.email?.toLowerCase().includes(q) ||
      r.referenceNumber?.toLowerCase().includes(q)
    );
  });

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>Payment Requests</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Review and approve manual premium payment submissions
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchRequests}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>

        {/* Summary cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Pending Review', value: requests.filter(r => r.status === 'pending').length, icon: <HourglassEmptyIcon color="warning" /> },
            { label: 'Approved', value: requests.filter(r => r.status === 'approved').length, icon: <CheckCircleIcon color="success" /> },
            { label: 'Rejected', value: requests.filter(r => r.status === 'rejected').length, icon: <CancelIcon color="error" /> },
          ].map((s) => (
            <Card key={s.label} sx={{ flex: '1 1 140px', borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {s.icon}
                <Box>
                  <Typography variant="h6" fontWeight={700}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}`} />
          <Tab label="Approved" />
          <Tab label="Rejected" />
        </Tabs>

        <TextField
          fullWidth
          placeholder="Search by username, email or reference number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ mb: 2 }}
          size="small"
        />

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Reference #</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Status</TableCell>
                      {tab !== 0 && <TableCell>Admin Notes</TableCell>}
                      {tab === 0 && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            {search ? 'No results match your search.' : `No ${STATUS_TABS[tab]} requests.`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {filtered.map((req) => (
                      <TableRow key={req.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={req.user?.profile?.currentAvatar} sx={{ width: 32, height: 32 }}>
                              {req.user?.username?.[0]?.toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>@{req.user?.username ?? '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">{req.user?.email ?? req.userId}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={methodLabel(req.paymentMethod)} size="small" variant="outlined" icon={<PaymentIcon />} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{req.referenceNumber}</Typography>
                            {req.screenshotUrl && (
                              <Tooltip title="View screenshot">
                                <IconButton size="small" onClick={() => window.open(req.screenshotUrl, '_blank')}>
                                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {req.notes && (
                            <Typography variant="caption" color="text.secondary" display="block">{req.notes}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {req.amount ? `₱${parseFloat(req.amount).toFixed(2)}` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(req.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(req.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={req.status} color={statusColor(req.status)} size="small" />
                        </TableCell>
                        {tab !== 0 && (
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {req.adminNotes || '—'}
                            </Typography>
                          </TableCell>
                        )}
                        {tab === 0 && (
                          <TableCell align="right">
                            <Tooltip title="Approve & activate premium">
                              <IconButton size="small" color="success" onClick={() => openAction('approve', req)}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject request">
                              <IconButton size="small" color="error" onClick={() => openAction('reject', req)}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Approve / Reject Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.type === 'approve' ? '✅ Approve Payment Request' : '❌ Reject Payment Request'}
        </DialogTitle>
        <DialogContent>
          {dialog.request && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>User:</strong> @{dialog.request.user?.username}</Typography>
              <Typography variant="body2"><strong>Method:</strong> {methodLabel(dialog.request.paymentMethod)}</Typography>
              <Typography variant="body2"><strong>Reference:</strong> {dialog.request.referenceNumber}</Typography>
              {dialog.request.amount && (
                <Typography variant="body2"><strong>Amount:</strong> ₱{parseFloat(dialog.request.amount).toFixed(2)}</Typography>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            label="Admin Notes (optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={3}
            placeholder={dialog.type === 'approve' ? 'e.g. Payment verified' : 'e.g. Reference number not found'}
            sx={{ mt: 1 }}
          />
          {dialog.type === 'approve' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Approving will immediately activate premium for this user.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false })} disabled={actioning}>Cancel</Button>
          <Button
            variant="contained"
            color={dialog.type === 'approve' ? 'success' : 'error'}
            onClick={handleAction}
            disabled={actioning}
            startIcon={actioning ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {actioning ? 'Processing…' : dialog.type === 'approve' ? 'Approve & Activate' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default PaymentRequests;
